import React, { useCallback, useEffect, useMemo, useState } from 'react';
import procesosAsincronicosService from '../../services/ProcesosAsincronicosService';
import './ProcesosAsincronicosModal.css';

const getProcesoId = (item) => item?.id || item?.Id || null;

const getProcesoTipo = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'importacionprofesores') return 'Importacion de profesores';
  if (normalized === 'importacionalumnos') return 'Importacion de alumnos';
  if (normalized === 'importacionareas') return 'Importacion de areas';
  if (normalized === 'importacionmaterias') return 'Importacion de materias';
  if (normalized === 'importacioncuros' || normalized === 'importacioncursos') return 'Importacion de cursos';
  if (normalized === 'comunicacionesemails') return 'Comunicaciones por email';
  return value || 'Proceso';
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getProcesoStatus = (item) => {
  const finalizado = item?.finalizado ?? item?.Finalizado ?? false;
  const iniciado = item?.iniciado ?? item?.Iniciado ?? false;
  const errores = item?.cantidadErrores ?? item?.CantidadErrores ?? 0;

  if (finalizado) {
    return {
      key: errores > 0 ? 'error' : 'done',
      label: errores > 0 ? 'Finalizado con errores' : 'Finalizado'
    };
  }

  if (iniciado) {
    return { key: 'running', label: 'En proceso' };
  }

  return { key: 'pending', label: 'Pendiente' };
};

const getDetalleStatus = (item) => {
  if (!item) {
    return { key: 'pending', label: 'Pendiente' };
  }

  if (item.exitoso ?? item.Exitoso) {
    return { key: 'done', label: item.estado || item.Estado || 'Ok' };
  }

  if (item.procesado ?? item.Procesado) {
    return { key: 'error', label: item.estado || item.Estado || 'Error' };
  }

  return { key: 'pending', label: item.estado || item.Estado || 'Pendiente' };
};

const normalizeEmailArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => String(item || '').trim())
    .filter(Boolean);
};

const ProcesosAsincronicosModal = ({ open, onClose, focusProcesoId = null, usePublicScope = false }) => {
  const [procesos, setProcesos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProcesoId, setSelectedProcesoId] = useState(null);
  const [selectedProcesoDetalle, setSelectedProcesoDetalle] = useState(null);
  const [selectedDetalleLoading, setSelectedDetalleLoading] = useState(false);
  const [selectedDetalleError, setSelectedDetalleError] = useState('');
  const [detalleSearch, setDetalleSearch] = useState('');

  const loadProcesos = useCallback(async ({ preserveSelection = true, preferredProcesoId = null } = {}) => {
    setLoading(true);
    setError('');

    try {
      const response = await procesosAsincronicosService.getProcesos(null, { usarEsquemaPublico: usePublicScope });
      const items = response?.items || response?.Items || [];
      const normalized = Array.isArray(items) ? items : [];
      setProcesos(normalized);
      setSelectedProcesoId((prev) => {
        if (preferredProcesoId && normalized.some((item) => getProcesoId(item) === preferredProcesoId)) {
          return preferredProcesoId;
        }
        if (preserveSelection && prev && normalized.some((item) => getProcesoId(item) === prev)) {
          return prev;
        }
        return getProcesoId(normalized[0]);
      });
    } catch (err) {
      setProcesos([]);
      setSelectedProcesoId(null);
      setSelectedProcesoDetalle(null);
      setSelectedDetalleError('');
      setError(err.message || 'No pudimos cargar los procesos asíncronos.');
    } finally {
      setLoading(false);
    }
  }, [usePublicScope]);

  const loadProcesoDetalle = useCallback(async (procesoId) => {
    if (!procesoId) {
      setSelectedProcesoDetalle(null);
      setSelectedDetalleError('');
      return;
    }

    setSelectedDetalleLoading(true);
    setSelectedDetalleError('');

    try {
      const response = await procesosAsincronicosService.getProcesoDetalle(procesoId, null, { usarEsquemaPublico: usePublicScope });
      setSelectedProcesoDetalle(response);
    } catch (err) {
      setSelectedProcesoDetalle(null);
      setSelectedDetalleError(err.message || 'No pudimos obtener el detalle del proceso.');
    } finally {
      setSelectedDetalleLoading(false);
    }
  }, [usePublicScope]);

  const refreshSelectedProcesoDetalle = useCallback(async () => {
    if (!selectedProcesoId) {
      return;
    }

    await loadProcesoDetalle(selectedProcesoId);
  }, [loadProcesoDetalle, selectedProcesoId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    loadProcesos({ preserveSelection: true, preferredProcesoId: focusProcesoId });
  }, [open, loadProcesos, focusProcesoId]);

  useEffect(() => {
    if (!open || !selectedProcesoId) {
      return;
    }

    loadProcesoDetalle(selectedProcesoId);
  }, [open, selectedProcesoId, loadProcesoDetalle]);

  useEffect(() => {
    if (!open) {
      setDetalleSearch('');
      return;
    }

    setDetalleSearch('');
  }, [open, selectedProcesoId]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const hasActiveProcesses = procesos.some((item) => !(item?.finalizado ?? item?.Finalizado ?? false));
    if (!hasActiveProcesses) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadProcesos({ preserveSelection: true, preferredProcesoId: focusProcesoId });
      refreshSelectedProcesoDetalle();
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [open, procesos, loadProcesos, refreshSelectedProcesoDetalle, focusProcesoId]);

  const selectedProceso = useMemo(() => {
    return procesos.find((item) => getProcesoId(item) === selectedProcesoId) || null;
  }, [procesos, selectedProcesoId]);

  const selectedProcesoData = useMemo(() => {
    return selectedProcesoDetalle?.proceso || selectedProcesoDetalle?.Proceso || selectedProceso || null;
  }, [selectedProceso, selectedProcesoDetalle]);

  const selectedProcesoCc = useMemo(() => {
    return normalizeEmailArray(selectedProcesoData?.cc || selectedProcesoData?.Cc);
  }, [selectedProcesoData]);

  const selectedProcesoCco = useMemo(() => {
    return normalizeEmailArray(selectedProcesoData?.cco || selectedProcesoData?.Cco);
  }, [selectedProcesoData]);

  const selectedDetalleRows = useMemo(() => {
    const items = selectedProcesoDetalle?.detalles || selectedProcesoDetalle?.Detalles || [];
    return Array.isArray(items) ? items : [];
  }, [selectedProcesoDetalle]);

  const filteredDetalleRows = useMemo(() => {
    const normalizedSearch = detalleSearch.trim().toLowerCase();
    if (!normalizedSearch) {
      return selectedDetalleRows;
    }

    return selectedDetalleRows.filter((item) => {
      const status = getDetalleStatus(item);
      const values = [
        item?.nombre || item?.Nombre || '',
        item?.apellido || item?.Apellido || '',
        item?.email || item?.Email || '',
        item?.estado || item?.Estado || '',
        item?.mensajeResultado || item?.MensajeResultado || '',
        item?.error || item?.Error || '',
        status.label
      ];

      return values.some((value) => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [selectedDetalleRows, detalleSearch]);

  const summary = useMemo(() => {
    const total = procesos.length;
    const running = procesos.filter((item) => {
      const finalizado = item?.finalizado ?? item?.Finalizado ?? false;
      const iniciado = item?.iniciado ?? item?.Iniciado ?? false;
      return iniciado && !finalizado;
    }).length;
    const withErrors = procesos.filter((item) => (item?.cantidadErrores ?? item?.CantidadErrores ?? 0) > 0).length;
    const finished = procesos.filter((item) => item?.finalizado ?? item?.Finalizado ?? false).length;
    return { total, running, withErrors, finished };
  }, [procesos]);

  if (!open) {
    return null;
  }

  return (
    <div className="procesos-modal-overlay" onClick={onClose}>
      <div
        className="procesos-modal-card"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="procesos-modal-header">
          <div>
            <p className="procesos-modal-eyebrow">Seguimiento</p>
            <h3>Procesos asíncronos</h3>
            <p className="procesos-modal-subtitle">
              Consulta importaciones, comunicaciones y cualquier otro proceso lanzado por tu usuario, sin entrar a un colegio.
            </p>
          </div>
          <div className="procesos-modal-actions">
            <button
              type="button"
              className="procesos-modal-refresh"
              onClick={async () => {
                await loadProcesos({ preserveSelection: true, preferredProcesoId: focusProcesoId });
                await refreshSelectedProcesoDetalle();
              }}
              disabled={loading}
            >
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} />
              <span>Actualizar</span>
            </button>
            <button type="button" className="procesos-modal-close" onClick={onClose} title="Cerrar">
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        <div className="procesos-modal-summary">
          <div className="procesos-summary-card">
            <span className="procesos-summary-label">Total</span>
            <strong>{summary.total}</strong>
          </div>
          <div className="procesos-summary-card">
            <span className="procesos-summary-label">En proceso</span>
            <strong>{summary.running}</strong>
          </div>
          <div className="procesos-summary-card">
            <span className="procesos-summary-label">Con errores</span>
            <strong>{summary.withErrors}</strong>
          </div>
          <div className="procesos-summary-card">
            <span className="procesos-summary-label">Finalizados</span>
            <strong>{summary.finished}</strong>
          </div>
        </div>

        <div className="procesos-modal-body">
          <section className="procesos-modal-list-panel">
            <div className="procesos-modal-panel-header">
              <h4>Listado</h4>
              <span>{procesos.length} procesos</span>
            </div>

            {loading && procesos.length === 0 && (
              <div className="procesos-modal-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Cargando procesos...</span>
              </div>
            )}

            {!loading && error && (
              <div className="procesos-modal-state error">
                <i className="fas fa-triangle-exclamation" />
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && procesos.length === 0 && (
              <div className="procesos-modal-state empty">
                <i className="fas fa-inbox" />
                <span>No hay procesos registrados para tu usuario.</span>
              </div>
            )}

            {!error && procesos.length > 0 && (
              <div className="procesos-modal-list">
                {procesos.map((item) => {
                  const id = getProcesoId(item);
                  const status = getProcesoStatus(item);
                  const progreso = item?.porcentajeAvance ?? item?.PorcentajeAvance ?? 0;
                  const total = item?.cantidadDeRegistros ?? item?.CantidadDeRegistros ?? 0;
                  const procesados = item?.cantidadProcesados ?? item?.CantidadProcesados ?? 0;
                  const colegio = item?.nombreColegio || item?.NombreColegio || 'Colegio';
                  const archivo = item?.nombreArchivo || item?.NombreArchivo || 'Sin archivo';

                  return (
                    <button
                      key={id}
                      type="button"
                      className={`procesos-list-item ${selectedProcesoId === id ? 'is-active' : ''}`}
                      onClick={() => setSelectedProcesoId(id)}
                    >
                      <div className="procesos-list-item-top">
                        <span className={`procesos-status-badge ${status.key}`}>{status.label}</span>
                        <span className="procesos-list-date">{formatDateTime(item?.fechaSolicitud || item?.FechaSolicitud)}</span>
                      </div>
                      <h5>{getProcesoTipo(item?.tipoProceso || item?.TipoProceso)}</h5>
                      <p className="procesos-list-scope">{colegio}</p>
                      <p className="procesos-list-file">{archivo}</p>
                      <div className="procesos-progress-row">
                        <div className="procesos-progress-track">
                          <div className="procesos-progress-fill" style={{ width: `${Math.max(0, Math.min(100, progreso))}%` }} />
                        </div>
                        <span>{progreso}%</span>
                      </div>
                      <div className="procesos-list-metrics">
                        <span>{procesados}/{total} procesados</span>
                        <span>{item?.cantidadErrores ?? item?.CantidadErrores ?? 0} errores</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="procesos-modal-detail-panel">
            <div className="procesos-modal-panel-header">
              <h4>Detalle</h4>
              {selectedProceso && (
                <span>
                  {filteredDetalleRows.length}
                  {detalleSearch.trim() ? ` de ${selectedDetalleRows.length}` : ''}
                  {' '}registros
                </span>
              )}
            </div>

            {!selectedProceso && !error && (
              <div className="procesos-modal-state empty">
                <i className="fas fa-arrow-left" />
                <span>Selecciona un proceso para ver el detalle.</span>
              </div>
            )}

            {selectedProcesoData && (
                <div className="procesos-detail-card">
                  <div className="procesos-detail-top">
                    <div>
                      <p className="procesos-detail-eyebrow">{selectedProcesoData?.nombreColegio || selectedProcesoData?.NombreColegio || 'Colegio'}</p>
                      <h4>{getProcesoTipo(selectedProcesoData?.tipoProceso || selectedProcesoData?.TipoProceso)}</h4>
                    </div>
                    <span className={`procesos-status-badge ${getProcesoStatus(selectedProcesoData).key}`}>
                      {getProcesoStatus(selectedProcesoData).label}
                    </span>
                  </div>

                <div className="procesos-detail-meta">
                  <div>
                    <span>Archivo</span>
                    <strong>{selectedProcesoData?.nombreArchivo || selectedProcesoData?.NombreArchivo || '—'}</strong>
                  </div>
                  <div>
                    <span>Solicitado</span>
                    <strong>{formatDateTime(selectedProcesoData?.fechaSolicitud || selectedProcesoData?.FechaSolicitud)}</strong>
                  </div>
                  <div>
                    <span>Inicio</span>
                    <strong>{formatDateTime(selectedProcesoData?.fechaInicio || selectedProcesoData?.FechaInicio)}</strong>
                  </div>
                  <div>
                    <span>Finalización</span>
                    <strong>{formatDateTime(selectedProcesoData?.fechaFinalizacion || selectedProcesoData?.FechaFinalizacion)}</strong>
                  </div>
                </div>

                <div className="procesos-detail-resume">
                  <div className="procesos-detail-pill">
                    <span>Registros</span>
                    <strong>{selectedProcesoData?.cantidadDeRegistros ?? selectedProcesoData?.CantidadDeRegistros ?? 0}</strong>
                  </div>
                  <div className="procesos-detail-pill">
                    <span>Procesados</span>
                    <strong>{selectedProcesoData?.cantidadProcesados ?? selectedProcesoData?.CantidadProcesados ?? 0}</strong>
                  </div>
                  <div className="procesos-detail-pill">
                    <span>Exitosos</span>
                    <strong>{selectedProcesoData?.cantidadExitosos ?? selectedProcesoData?.CantidadExitosos ?? 0}</strong>
                  </div>
                  <div className="procesos-detail-pill">
                    <span>Errores</span>
                    <strong>{selectedProcesoData?.cantidadErrores ?? selectedProcesoData?.CantidadErrores ?? 0}</strong>
                  </div>
                </div>

                {(selectedProcesoCc.length > 0 || selectedProcesoCco.length > 0) && (
                  <div className="procesos-recipient-groups">
                    {selectedProcesoCc.length > 0 && (
                      <div className="procesos-recipient-group">
                        <span>CC</span>
                        <div className="procesos-recipient-chips">
                          {selectedProcesoCc.map((email) => (
                            <span key={`cc-${email}`} className="procesos-recipient-chip">{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedProcesoCco.length > 0 && (
                      <div className="procesos-recipient-group">
                        <span>CCO</span>
                        <div className="procesos-recipient-chips">
                          {selectedProcesoCco.map((email) => (
                            <span key={`cco-${email}`} className="procesos-recipient-chip">{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!selectedDetalleLoading && !selectedDetalleError && selectedDetalleRows.length > 0 && (
                  <div className="procesos-detail-filter-bar">
                    <div className="procesos-detail-search">
                      <i className="fas fa-search" />
                      <input
                        type="text"
                        value={detalleSearch}
                        onChange={(e) => setDetalleSearch(e.target.value)}
                        placeholder="Buscar por nombre, email, estado o resultado"
                        aria-label="Buscar en el detalle del proceso"
                      />
                    </div>
                    {detalleSearch.trim() && (
                      <button
                        type="button"
                        className="procesos-detail-search-clear"
                        onClick={() => setDetalleSearch('')}
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                )}

                {selectedDetalleLoading && (
                  <div className="procesos-modal-state">
                    <i className="fas fa-spinner fa-spin" />
                    <span>Cargando detalle del proceso...</span>
                  </div>
                )}

                {!selectedDetalleLoading && selectedDetalleError && (
                  <div className="procesos-modal-state error">
                    <i className="fas fa-triangle-exclamation" />
                    <span>{selectedDetalleError}</span>
                  </div>
                )}

                {!selectedDetalleLoading && !selectedDetalleError && selectedDetalleRows.length === 0 && (
                  <div className="procesos-modal-state empty">
                    <i className="fas fa-file-circle-minus" />
                    <span>Este proceso no tiene registros de detalle cargados.</span>
                  </div>
                )}

                {!selectedDetalleLoading && !selectedDetalleError && selectedDetalleRows.length > 0 && filteredDetalleRows.length === 0 && (
                  <div className="procesos-modal-state empty">
                    <i className="fas fa-magnifying-glass" />
                    <span>No hay registros que coincidan con la búsqueda.</span>
                  </div>
                )}

                {!selectedDetalleLoading && !selectedDetalleError && filteredDetalleRows.length > 0 && (
                  <div className="procesos-detail-table-wrap">
                    <table className="procesos-detail-table">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Destino</th>
                          <th>Estado</th>
                          <th>Resultado</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDetalleRows.map((item) => {
                          const status = getDetalleStatus(item);
                          const nombre = [item?.nombre || item?.Nombre, item?.apellido || item?.Apellido]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          const email = item?.email || item?.Email || '—';
                          const resultado = item?.mensajeResultado || item?.MensajeResultado || item?.error || item?.Error || '—';

                          return (
                            <tr key={item?.id || item?.Id}>
                              <td>{item?.numeroFila ?? item?.NumeroFila ?? '—'}</td>
                              <td>
                                <div className="procesos-destino-cell">
                                  <strong>{nombre || 'Sin nombre'}</strong>
                                  <span>{email}</span>
                                </div>
                              </td>
                              <td>
                                <span className={`procesos-status-badge ${status.key}`}>{status.label}</span>
                              </td>
                              <td>{resultado}</td>
                              <td>{formatDateTime(item?.fechaProcesamiento || item?.FechaProcesamiento)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProcesosAsincronicosModal;
